import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { MagnifyingGlass, MapPin, Star, Clock, SlidersHorizontal, Lightning } from "@phosphor-icons/react";
import ImgFallback from "@/components/ImgFallback";

const QUICK_CHIPS = ["Tonight", "Tomorrow", "This Week", "Skin Fade", "Braids", "Colour", "Beard", "Curly Hair"];
const HAIR_TYPES = ["short", "fade", "beard", "curly", "long", "braids", "colour"];

export default function Explore() {
  const [studios, setStudios] = useState([]);
  const [q, setQ] = useState("");
  const [chip, setChip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    time: "any",           // any | tonight | tomorrow | week
    distanceMax: 15,       // km
    service: "",
    priceMax: 200,
    ratingMin: 4.5,
    hairType: "",
    instantBook: false,
  });

  useEffect(() => {
    api.get("/studios").then((r) => setStudios(r.data)).finally(() => setLoading(false));
  }, []);

  const applyChip = (c) => {
    setChip(chip === c ? null : c);
    // Chip = shortcut for filters
    const upd = { ...filters };
    if (c === "Tonight") upd.time = "tonight";
    else if (c === "Tomorrow") upd.time = "tomorrow";
    else if (c === "This Week") upd.time = "week";
    else if (["Skin Fade", "Braids", "Colour", "Beard", "Curly Hair"].includes(c)) upd.service = c;
    setFilters(upd);
  };

  const filtered = useMemo(() => {
    let list = studios;
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(n) ||
          s.professional_name?.toLowerCase().includes(n) ||
          s.tagline?.toLowerCase().includes(n) ||
          s.city.toLowerCase().includes(n) ||
          s.area.toLowerCase().includes(n) ||
          s.services?.some((sv) => sv.name.toLowerCase().includes(n))
      );
    }
    if (filters.service) {
      const n = filters.service.toLowerCase();
      list = list.filter((s) => s.services?.some((sv) => sv.name.toLowerCase().includes(n)));
    }
    if (filters.hairType) {
      list = list.filter((s) => (s.hair_types || []).includes(filters.hairType));
    }
    list = list.filter((s) => (s.distance_km ?? 99) <= filters.distanceMax);
    list = list.filter((s) => (s.starting_price ?? 0) <= filters.priceMax);
    list = list.filter((s) => (s.rating ?? 0) >= filters.ratingMin);
    if (filters.instantBook) list = list.filter((s) => s.instant_book);
    if (filters.time === "tonight") list = list.filter((s) => s.tonight_slot);
    return list;
  }, [studios, q, filters]);

  const clearAll = () => {
    setQ(""); setChip(null);
    setFilters({ time: "any", distanceMax: 15, service: "", priceMax: 200, ratingMin: 4.5, hairType: "", instantBook: false });
  };

  return (
    <div className="max-w-6xl mx-auto px-5 pt-8 pb-24" data-testid="explore-page">
      <p className="overline mb-2">Explore Studios</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter text-cream">What are you looking for?</h1>

      <div className="mt-8 flex items-center gap-3 border-b border-cream/20 pb-2">
        <MagnifyingGlass size={20} color="#C5A059" />
        <input
          data-testid="explore-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a cut, colour, city, area, Professional or Studio"
          className="bg-transparent flex-1 outline-none text-cream placeholder:text-cream/40 py-3"
        />
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          data-testid="filters-toggle"
          className="chip hover:border-cream/40"
        >
          <SlidersHorizontal size={12} /> Filters
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {QUICK_CHIPS.map((c) => (
          <button
            key={c}
            onClick={() => applyChip(c)}
            data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
            className={`chip transition-colors ${chip === c ? "border-gold bg-gold text-ink font-semibold" : "hover:border-cream/40"}`}
          >
            {c}
          </button>
        ))}
      </div>

      {filtersOpen && (
        <div className="mt-6 border border-cream/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="filters-panel">
          <div>
            <p className="overline mb-2">Time</p>
            <div className="flex flex-wrap gap-2">
              {[["any", "Anytime"], ["tonight", "Tonight"], ["tomorrow", "Tomorrow"], ["week", "This week"]].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => setFilters({ ...filters, time: v })}
                  data-testid={`filter-time-${v}`}
                  className={`chip ${filters.time === v ? "border-gold bg-gold text-ink font-semibold" : ""}`}
                >{l}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="overline mb-2">Distance up to {filters.distanceMax} km</p>
            <input type="range" min={1} max={50} value={filters.distanceMax} onChange={(e) => setFilters({ ...filters, distanceMax: Number(e.target.value) })} className="w-full accent-[#C5A059]" data-testid="filter-distance" />
          </div>
          <div>
            <p className="overline mb-2">Service</p>
            <div className="flex flex-wrap gap-2">
              {["", "Skin Fade", "Taper Fade", "Braids", "Colour", "Cut", "Beard"].map((s) => (
                <button
                  key={s || "any"}
                  onClick={() => setFilters({ ...filters, service: s })}
                  data-testid={`filter-service-${(s || "any").replace(/\s+/g, "-").toLowerCase()}`}
                  className={`chip ${filters.service === s ? "border-gold bg-gold text-ink font-semibold" : ""}`}
                >{s || "Any"}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="overline mb-2">Price up to ${filters.priceMax}</p>
            <input type="range" min={20} max={300} step={5} value={filters.priceMax} onChange={(e) => setFilters({ ...filters, priceMax: Number(e.target.value) })} className="w-full accent-[#C5A059]" data-testid="filter-price" />
          </div>
          <div>
            <p className="overline mb-2">Rating from {filters.ratingMin.toFixed(1)}★</p>
            <input type="range" min={3.0} max={5.0} step={0.1} value={filters.ratingMin} onChange={(e) => setFilters({ ...filters, ratingMin: Number(e.target.value) })} className="w-full accent-[#C5A059]" data-testid="filter-rating" />
          </div>
          <div>
            <p className="overline mb-2">Hair type / style</p>
            <div className="flex flex-wrap gap-2">
              {["", ...HAIR_TYPES].map((h) => (
                <button
                  key={h || "any"}
                  onClick={() => setFilters({ ...filters, hairType: h })}
                  data-testid={`filter-hair-${h || "any"}`}
                  className={`chip capitalize ${filters.hairType === h ? "border-gold bg-gold text-ink font-semibold" : ""}`}
                >{h || "Any"}</button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 flex items-center justify-between">
            <label className="flex items-center gap-3 cursor-pointer" data-testid="filter-instant">
              <input type="checkbox" checked={filters.instantBook} onChange={(e) => setFilters({ ...filters, instantBook: e.target.checked })} className="accent-[#C5A059] w-5 h-5" />
              <span className="text-sm text-cream/85 flex items-center gap-1"><Lightning size={14} color="#C5A059" weight="fill" /> Instant Book only</span>
            </label>
            <button onClick={clearAll} data-testid="filter-clear" className="text-xs uppercase tracking-[0.2em] text-cream/60 hover:text-gold">Clear all</button>
          </div>
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.2em] text-cream/50" data-testid="explore-count">
          {loading ? "Searching…" : `${filtered.length} Studios`}
        </p>
        <Link to="/map" className="text-xs uppercase tracking-[0.2em] text-cream/60 hover:text-gold" data-testid="link-map">Map view</Link>
      </div>

      {!loading && filtered.length === 0 && (
        <div className="mt-16 border border-cream/10 p-10" data-testid="explore-empty">
          <p className="overline mb-3">Nothing matches — yet</p>
          <p className="text-cream/70 mb-6">Try widening the search:</p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="empty-widen-distance" onClick={() => setFilters({ ...filters, distanceMax: Math.min(50, filters.distanceMax + 10) })} className="chip hover:border-gold">Widen distance +10 km</button>
            <button data-testid="empty-any-time" onClick={() => setFilters({ ...filters, time: "any" })} className="chip hover:border-gold">Any time this week</button>
            <button data-testid="empty-similar" onClick={() => setFilters({ ...filters, service: "" })} className="chip hover:border-gold">Try similar services</button>
            <button data-testid="empty-clear" onClick={clearAll} className="chip hover:border-gold">Reset everything</button>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {filtered.map((s) => (
          <Link
            key={s.id} to={`/studio/${s.id}`}
            data-testid={`explore-studio-${s.id}`}
            className="group border border-cream/10 hover:border-gold/50 transition-colors"
          >
            <div className="relative overflow-hidden">
              <ImgFallback
                src={s.hero_look || s.portfolio?.[0] || s.cover_image}
                alt={`${s.name} — haircut`}
                className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute top-3 left-3 flex gap-2">
                {s.tonight_slot && (
                  <span className="chip bg-ink/70 backdrop-blur">
                    <Clock size={12} weight="fill" color="#C5A059" /> Tonight · {s.tonight_slot}
                  </span>
                )}
                {s.instant_book && (
                  <span className="chip bg-gold/90 text-ink border-transparent font-semibold text-[10px]">
                    <Lightning size={10} weight="fill" /> Instant
                  </span>
                )}
              </div>
              <div className="absolute top-3 right-3 chip bg-ink/70 backdrop-blur">
                <Star size={12} weight="fill" color="#C5A059" /> {s.rating}
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-xl tracking-tight text-cream">{s.name}</h3>
                  <p className="text-xs text-cream/60">{s.professional_name} · {s.completed_cuts}+ verified cuts</p>
                </div>
                <p className="text-xs text-cream/50 flex items-center gap-1 whitespace-nowrap">
                  <MapPin size={12} weight="fill" color="#C5A059" />{s.area} · {s.distance_km}km
                </p>
              </div>
              <p className="mt-3 text-sm text-cream/80 leading-relaxed">{s.tagline}</p>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {(s.services || []).slice(0, 2).map((sv) => (
                    <span key={sv.id} className="chip">{sv.name} · ${sv.price}</span>
                  ))}
                </div>
                <p className="text-xs text-gold whitespace-nowrap">from ${s.starting_price}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
